/**
 * /payroll-l4/wage-determinations — WD catalog index.
 *
 * Lists all wage determinations for the active entity. Click a row to drill
 * into the detail page (header + rate editor). "+ New WD" opens a dialog
 * to create one.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { api, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { FileDropZone } from "@/components/FileDropZone";
import type {
  WageDetermination,
  WdParseResult,
  WdSource,
  WdStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll-l4/wage-determinations")({
  component: WdIndexPage,
});

const STATUS_COLOR: Record<WdStatus, "default" | "success" | "warning" | "error"> = {
  active: "success",
  superseded: "warning",
  archived: "default",
};

const PROJECT_TYPES = ["building", "highway", "heavy", "residential"];
const SOURCES: WdSource[] = ["dol", "sam_gov", "state_dir", "manual"];
const SOURCE_LABELS: Record<string, string> = {
  dol: "Federal — Dept. of Labor",
  sam_gov: "Federal — SAM.gov",
  state_dir: "State Dept. of Industrial Relations",
  manual: "Manual entry",
};

function WdIndexPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<WdStatus | "">("");
  const [stateFilter, setStateFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: wds = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "wage-determinations", statusFilter, stateFilter],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status_filter", statusFilter);
      if (stateFilter) qs.set("state", stateFilter.toUpperCase());
      const url = `/api/payroll-l4/wage-determinations${qs.toString() ? "?" + qs : ""}`;
      return (await api(url)) as WageDetermination[];
    },
  });

  // Default form values for the create dialog. Dates start BLANK — they come
  // from the DOL wage-determination document, not today, so we don't pre-fill
  // a wrong-but-plausible value the user might submit by accident.
  const [form, setForm] = useState({
    wd_number: "",
    modification_number: "",
    decision_date: "",
    effective_date: "",
    expiration_date: "",
    source: "dol" as WdSource,
    project_type: "building",
    state_code: "",
    counties: "",
    source_url: "",
    notes: "",
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        wd_number: form.wd_number.trim(),
        decision_date: form.decision_date,
        effective_date: form.effective_date,
        source: form.source,
        project_type: form.project_type,
        state_code: form.state_code.trim().toUpperCase(),
        counties: form.counties
          ? form.counties.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      if (form.modification_number.trim()) payload.modification_number = form.modification_number.trim();
      if (form.expiration_date) payload.expiration_date = form.expiration_date;
      if (form.source_url.trim()) payload.source_url = form.source_url.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      return (await api(`/api/payroll-l4/wage-determinations`, {
        method: "POST",
        body: payload,
      })) as { id: string };
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
      setCreateOpen(false);
      navigate({
        to: "/payroll-l4/wage-determinations/$wdId" as never,
        params: { wdId: created.id } as never,
      });
    },
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Wage Determinations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Federal + state prevailing-wage rate tables for Davis-Bacon projects.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-l4/step-2-wd-catalog.md" label="Wage Determinations help" />
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select label="Status" size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WdStatus | "")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="superseded">Superseded</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </TextField>
          <TextField
            label="State" size="small" value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            placeholder="e.g. CA"
            sx={{ width: 90 }}
            inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload PDF
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New WD
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(error)}</Alert>}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>WD Number</TableCell>
                <TableCell>Mod</TableCell>
                <TableCell>State / Counties</TableCell>
                <TableCell>Project Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Effective</TableCell>
                <TableCell align="center">Rates</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wds.map((wd) => (
                <TableRow
                  key={wd.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate({
                    to: "/payroll-l4/wage-determinations/$wdId" as never,
                    params: { wdId: wd.id } as never,
                  })}
                >
                  <TableCell><strong>{wd.wd_number}</strong></TableCell>
                  <TableCell>{wd.modification_number || "—"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip size="small" label={wd.state_code} />
                      <Typography variant="caption" color="text.secondary">
                        {wd.counties.length > 0
                          ? wd.counties.slice(0, 2).join(", ") + (wd.counties.length > 2 ? "…" : "")
                          : "(no counties)"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{wd.project_type}</TableCell>
                  <TableCell><Chip size="small" label={SOURCE_LABELS[wd.source] ?? wd.source} variant="outlined" /></TableCell>
                  <TableCell>{wd.effective_date}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      <strong>{wd.active_rate_count}</strong>
                      {" "}<span style={{ opacity: 0.6 }}>/ {wd.rate_count}</span>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={wd.status} color={STATUS_COLOR[wd.status]} />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      component={Link}
                      to={"/payroll-l4/wage-determinations/$wdId" as never}
                      params={{ wdId: wd.id } as never}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {wds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No wage determinations yet. Click <strong>+ New WD</strong> to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Upload-PDF dialog ── */}
      <UploadWdDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={(wdId) => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
          setUploadOpen(false);
          navigate({
            to: "/payroll-l4/wage-determinations/$wdId" as never,
            params: { wdId } as never,
          });
        }}
      />

      {/* ── Create dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Wage Determination</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField
              label="WD Number" required size="small" value={form.wd_number}
              onChange={(e) => setForm((f) => ({ ...f, wd_number: e.target.value }))}
              placeholder="CA20240001"
            />
            <TextField
              label="Modification Number" size="small" value={form.modification_number}
              onChange={(e) => setForm((f) => ({ ...f, modification_number: e.target.value }))}
              placeholder="0, Mod 1, …"
            />
            <TextField
              label="Decision Date" type="date" required size="small" value={form.decision_date}
              InputLabelProps={{ shrink: true }}
              helperText="From the wage determination document"
              onChange={(e) => setForm((f) => ({ ...f, decision_date: e.target.value }))}
            />
            <TextField
              label="Effective Date" type="date" required size="small" value={form.effective_date}
              InputLabelProps={{ shrink: true }}
              helperText="From the wage determination document"
              onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
            />
            <TextField
              label="Expiration Date" type="date" size="small" value={form.expiration_date}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))}
              helperText="Leave blank if open-ended"
            />
            <TextField
              select label="Source" required size="small" value={form.source}
              onChange={(e) => setForm((f) => ({ ...f, source: e.target.value as WdSource }))}
            >
              {SOURCES.map((s) => <MenuItem key={s} value={s}>{SOURCE_LABELS[s] ?? s}</MenuItem>)}
            </TextField>
            <TextField
              select label="Project Type" required size="small" value={form.project_type}
              onChange={(e) => setForm((f) => ({ ...f, project_type: e.target.value }))}
            >
              {PROJECT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label="State" required size="small" value={form.state_code}
              onChange={(e) => setForm((f) => ({ ...f, state_code: e.target.value }))}
              placeholder="CA"
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            />
            <TextField
              label="Counties (comma-separated)" size="small" value={form.counties}
              onChange={(e) => setForm((f) => ({ ...f, counties: e.target.value }))}
              placeholder="Los Angeles, Orange, Riverside"
              sx={{ gridColumn: "span 2" }}
            />
            <TextField
              label="Source URL" size="small" value={form.source_url}
              onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))}
              placeholder="https://sam.gov/wage-determinations/CA20240001"
              sx={{ gridColumn: "span 2" }}
            />
            <TextField
              label="Notes" size="small" multiline rows={2} value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              sx={{ gridColumn: "span 2" }}
            />
          </Box>
          {create.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage(create.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.wd_number.trim() || !form.state_code.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// UploadWdDialog — Step 3: PDF parse → WD with draft rates
//
// The dialog accepts a single PDF + an optional set of header overrides
// the admin can use to pre-fill fields the parser may miss (state_code +
// project_type are the most common). On submit we POST multipart to
// /api/payroll-l4/wage-determinations/parse-pdf which runs PaddleOCR-VL
// (Edge) → Claude tool extraction, then materializes the WD with all rate
// rows in `status='draft'`. The admin lands on the detail page and clicks
// "Promote drafts" after eyeballing the parsed rates.
// ─────────────────────────────────────────────────────────────────────────
function UploadWdDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (wdId: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [stateCode, setStateCode] = useState("");
  const [projectType, setProjectType] = useState("");
  const [source, setSource] = useState<WdSource | "">("");
  const [result, setResult] = useState<WdParseResult | null>(null);

  // Reset all local state every time the dialog opens.
  const reset = () => {
    setFile(null);
    setStateCode("");
    setProjectType("");
    setSource("");
    setResult(null);
  };

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a PDF first");
      const fd = new FormData();
      fd.append("file", file);
      const overrides: Record<string, unknown> = {};
      if (stateCode.trim()) overrides.state_code = stateCode.trim().toUpperCase();
      if (projectType) overrides.project_type = projectType;
      if (source) overrides.source = source;
      fd.append("overrides_json", JSON.stringify(overrides));
      return (await api(`/api/payroll-l4/wage-determinations/parse-pdf`, {
        method: "POST",
        body: fd,
      })) as WdParseResult;
    },
    onSuccess: (r) => {
      setResult(r);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!upload.isPending) {
          reset();
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upload Wage Determination PDF</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drop a DOL, SAM.gov, or state DIR PDF. The parser extracts the
          header + per-classification rates; rates land as <strong>drafts</strong>{" "}
          for you to promote after a quick eyeball check.
        </Typography>

        <Stack spacing={2} sx={{ mb: 2 }}>
          <FileDropZone
            file={file}
            onFile={(f) => { setFile(f); setResult(null); }}
            onClear={() => { setFile(null); setResult(null); }}
            accept="application/pdf"
            label="Drop or click to select WD PDF"
            sublabel="DOL · SAM.gov · state DIR"
            disabled={upload.isPending}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Optional overrides — fill these only if the parser commonly misreads
          the document for this jurisdiction.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
          <TextField
            label="State"
            size="small"
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value)}
            inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
          />
          <TextField
            select
            label="Project Type"
            size="small"
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
          >
            <MenuItem value="">(parser-decides)</MenuItem>
            {PROJECT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Source"
            size="small"
            value={source}
            onChange={(e) => setSource(e.target.value as WdSource | "")}
          >
            <MenuItem value="">(parser-decides)</MenuItem>
            {SOURCES.map((s) => <MenuItem key={s} value={s}>{SOURCE_LABELS[s] ?? s}</MenuItem>)}
          </TextField>
        </Box>

        {upload.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(upload.error)}
          </Alert>
        )}
        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Parsed via <strong>{result.parser}</strong> ·{" "}
            confidence {(result.extraction_confidence * 100).toFixed(0)}% ·{" "}
            {result.wage_determination.rates.length} draft rate
            {result.wage_determination.rates.length === 1 ? "" : "s"}
            {result.skipped_rate_rows > 0 && (
              <> · skipped {result.skipped_rate_rows} unparseable</>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={upload.isPending}
        >
          Cancel
        </Button>
        {result ? (
          <Button
            variant="contained"
            onClick={() => {
              const wdId = result.wage_determination.id;
              reset();
              onSuccess(wdId);
            }}
          >
            Open WD →
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={upload.isPending ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => upload.mutate()}
            disabled={!file || upload.isPending}
          >
            {upload.isPending ? "Parsing…" : "Parse + Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
